// services/chatInterfaceService.ts - Service for managing calls within the Chat interface (internal use case)
/* eslint-disable @typescript-eslint/no-explicit-any */

import { apiClient, baseUrl } from './api';
import { ChatMessage } from '@/types/ChatMessage';
import { AssistantType } from '@/types/ChatInterface';
import {
  AsyncExecution,
  AsyncExecutionStartResponse,
  ExecutionStatusResponse,
  ExecutionCancelResponse,
  ExecutionProgressEvent,
  StreamConnection,
  DEFAULT_ASYNC_CONFIG,
  AsyncExecutionHandlers,
} from '@/types/AsyncExecution';
import {
  executionUtils,
  ExecutionProgressTracker,
  ConnectionStateManager,
  processExecutionStream,
} from './executionCore';
import { executionResponseUtils } from '@/utils/executionResponseUtils';
import { getAccessToken } from '@/utils/tokenStorage';
import {
  makeSSERequest,
  processOpenAIStream,
  OpenAIStreamEventHandlers,
  processStructuredStream,
  StructuredSSEEvent,
  StructuredStreamEventHandlers,
  TimeoutError,
} from './sseService';

// =================== Types & Interfaces ===================

export interface ChatInterfaceConversation {
  id: string;
  title: string;
  chatbot_id: string;
  chatbot_type: AssistantType;
  chatbot_name: string;
  created_at: string;
  updated_at: string;
  last_message_preview?: string;
}

export interface ChatInterfaceConversationCreate {
  title: string;
  chatbot_id: string;
}

export interface SimpleChatbotMessageRequest {
  messages: ChatMessage[];
}

export interface FlowMessageRequest {
  input_data: Record<string, any>;
}

export interface InputFieldInfo {
  name: string;
  type: string;
  is_mandatory: boolean;
}

export interface ChatbotInputFieldsInfo {
  chatbot_id: string;
  chatbot_name: string;
  chatbot_type: string;
  input_fields: InputFieldInfo[];
  has_additional_inputs: boolean;
  is_published: boolean;
  version_number?: number;
}

// =================== Section 1: Basic API Calls ===================

export const fetchChatbotInputFields = async (chatbotId: string): Promise<ChatbotInputFieldsInfo> => {
  const response = await apiClient.get(`/chat-interface/chatbots/${chatbotId}/input-fields`);
  return response.data;
};

export const fetchChatInterfaceConversations = async (): Promise<ChatInterfaceConversation[]> => {
  const response = await apiClient.get('/chat-interface/conversations');
  return response.data.conversations || response.data;
};

export const fetchChatInterfaceHistory = async (conversationId: string): Promise<ChatMessage[]> => {
  const response = await apiClient.get(`/chat-interface/conversations/${conversationId}/history`);
  return response.data.history || response.data;
};

export const createChatInterfaceConversation = async (data: ChatInterfaceConversationCreate): Promise<string> => {
  const response = await apiClient.post('/chat-interface/conversations', data);
  return response.data.conversation_id;
};

export const renameChatInterfaceConversation = async (conversationId: string, title: string): Promise<void> => {
  await apiClient.put(`/chat-interface/conversations/${conversationId}`, { title });
};

export const deleteChatInterfaceConversation = async (conversationId: string): Promise<void> => {
  await apiClient.delete(`/chat-interface/conversations/${conversationId}`);
};

// =================== Section 2: Sync Streaming (SSE) ===================

export interface SimpleSSEEventHandlers extends OpenAIStreamEventHandlers {
  onTimeoutError?: (error: TimeoutError) => void;
}

export const streamSimpleChatMessage = (
  conversationId: string,
  messageData: SimpleChatbotMessageRequest,
  eventHandlers: SimpleSSEEventHandlers
): (() => void) => {
  const controller = new AbortController();

  makeSSERequest(
    `${baseUrl}/chat-interface/conversations/${conversationId}/simple/message`,
    messageData,
    controller.signal
  )
  .then(async (response) => {
    if (!response.body) {
      eventHandlers?.onError?.(new Error('Response body is null'));
      return;
    }
    await processOpenAIStream(response.body.getReader(), eventHandlers, controller.signal);
  })
  .catch(error => {
    if (error.name !== 'AbortError') {
      if ('isTimeout' in error && error.isTimeout) {
        eventHandlers?.onTimeoutError?.(error as TimeoutError);
      } else {
        eventHandlers?.onError?.(error instanceof Error ? error : new Error(String(error)));
      }
    }
  });

  return () => controller.abort();
};

export interface FlowSSEEventHandlers {
  onStreamOpen?: () => void;
  onNodeStarted?: (event: StructuredSSEEvent) => void;
  onNodeCompleted?: (event: StructuredSSEEvent) => void;
  onNodeError?: (event: StructuredSSEEvent) => void;
  onFinalAssistantMessage?: (fullMessage: string, event: StructuredSSEEvent) => void;
  onExecutionError?: (event: StructuredSSEEvent) => void;
  onStreamClose?: (error?: Error | TimeoutError) => void;
  onTimeoutError?: (error: TimeoutError) => void;
}

const createFlowStreamAdapter = (handlers: FlowSSEEventHandlers): StructuredStreamEventHandlers => ({
  onStreamOpen: () => handlers.onStreamOpen?.(),
  onEvent: (event: StructuredSSEEvent) => {
    switch (event.type) {
      case 'node_started':
        handlers.onNodeStarted?.(event);
        break;
      case 'node_completed':
        handlers.onNodeCompleted?.(event);
        break;
      case 'node_error':
        handlers.onNodeError?.(event);
        break;
      case 'execution_completed': {
        const responseText = executionResponseUtils.extractResponse(event.final_output);
        handlers.onFinalAssistantMessage?.(responseText || 'Response completed.', event);
        break;
      }
      case 'execution_error':
        handlers.onExecutionError?.(event);
        break;
    }
  },
  onStreamClose: (error?: Error | TimeoutError) => {
    if (error && 'isTimeout' in error) {
      handlers.onTimeoutError?.(error as TimeoutError);
    } else {
      handlers.onStreamClose?.(error);
    }
  },
  onTimeoutError: (error: TimeoutError) => handlers.onTimeoutError?.(error),
});

export const streamChatflowMessage = (
  conversationId: string,
  messageData: FlowMessageRequest,
  handlers: FlowSSEEventHandlers
): (() => void) => {
  const controller = new AbortController();
  const adapter = createFlowStreamAdapter(handlers);

  makeSSERequest(
    `${baseUrl}/chat-interface/conversations/${conversationId}/chatflow/message`,
    messageData,
    controller.signal
  )
  .then(async (response) => {
    if (!response.body) throw new Error('Response body is null');
    await processStructuredStream(response.body.getReader(), adapter, controller.signal);
  })
  .catch(error => {
    if (error.name !== 'AbortError') {
      adapter.onStreamClose?.(error instanceof Error ? error : new Error(String(error)));
    }
  });

  return () => controller.abort();
};

export const streamWorkflowMessage = (
  conversationId: string,
  messageData: FlowMessageRequest,
  handlers: FlowSSEEventHandlers
): (() => void) => {
  const controller = new AbortController();
  const adapter = createFlowStreamAdapter(handlers);

  makeSSERequest(
    `${baseUrl}/chat-interface/conversations/${conversationId}/workflow/message`,
    messageData,
    controller.signal
  )
  .then(async (response) => {
    if (!response.body) throw new Error('Response body is null');
    await processStructuredStream(response.body.getReader(), adapter, controller.signal);
  })
  .catch(error => {
    if (error.name !== 'AbortError') {
      adapter.onStreamClose?.(error instanceof Error ? error : new Error(String(error)));
    }
  });

  return () => controller.abort();
};

// =================== Section 3: Async Execution API ===================

export const startAsyncSimpleExecution = async (
  conversationId: string,
  messageData: SimpleChatbotMessageRequest
): Promise<AsyncExecutionStartResponse> => {
  const response = await apiClient.post(
    `/chat-interface/conversations/${conversationId}/simple/message/async`,
    messageData
  );
  return response.data;
};

export const startAsyncChatflowExecution = async (
  conversationId: string,
  messageData: FlowMessageRequest
): Promise<AsyncExecutionStartResponse> => {
  const response = await apiClient.post(
    `/chat-interface/conversations/${conversationId}/chatflow/message/async`,
    messageData
  );
  return response.data;
};

export const startAsyncWorkflowExecution = async (
  conversationId: string,
  messageData: FlowMessageRequest
): Promise<AsyncExecutionStartResponse> => {
  const response = await apiClient.post(
    `/chat-interface/conversations/${conversationId}/workflow/message/async`,
    messageData
  );
  return response.data;
};

export const getExecutionStatus = async (executionId: string): Promise<ExecutionStatusResponse> => {
  const response = await apiClient.get(`/chat-interface/executions/${executionId}/status`);
  return response.data;
};

export const cancelExecution = async (executionId: string): Promise<ExecutionCancelResponse> => {
  const response = await apiClient.delete(`/chat-interface/executions/${executionId}`);
  return response.data;
};

// =================== Section 4: Async Execution Manager ===================

class ChatInterfaceAsyncManager {
  private progressTracker = new ExecutionProgressTracker();
  private connectionManager = new ConnectionStateManager();
  private activeStreams = new Map<string, AbortController>();
  private retryTimeouts = new Map<string, NodeJS.Timeout>();

  /**
   * Start async execution with resilient streaming
   */
  async startAsyncExecution(
    conversationId: string,
    assistantType: AssistantType,
    messageData: SimpleChatbotMessageRequest | FlowMessageRequest,
    handlers: AsyncExecutionHandlers
  ): Promise<string> {
    
    // Start the execution
    let response: AsyncExecutionStartResponse;
    
    if (assistantType === 'simple') {
      response = await startAsyncSimpleExecution(conversationId, messageData as SimpleChatbotMessageRequest);
    } else if (assistantType === 'chatflow') {
      response = await startAsyncChatflowExecution(conversationId, messageData as FlowMessageRequest);
    } else {
      response = await startAsyncWorkflowExecution(conversationId, messageData as FlowMessageRequest);
    }

    // Track execution
    const execution: AsyncExecution = {
      execution_id: response.execution_id,
      status: 'PENDING',
      progress: 0,
      current_node: null,
      completed_nodes: 0,
      total_nodes: 0,
      final_output: null,
      error_message: null,
      started_at: new Date().toISOString(),
      completed_at: null,
      execution_source: 'chatbot',
      conversation_id: conversationId,
      assistant_type: assistantType,
    };

    this.progressTracker.trackExecution(execution);
    handlers.onExecutionStarted?.(execution);

    // Start resilient streaming immediately (don't await to avoid blocking)
    this.startResilientStream(response.execution_id, handlers).catch(error => {
      handlers.onConnectionError?.(error);
    });

    return response.execution_id;
  }

  /**
   * Start streaming for an existing execution (public method)
   */
  async startStreamForExecution(executionId: string, handlers: AsyncExecutionHandlers): Promise<void> {
    // Get or create execution tracking
    let execution = this.progressTracker.getExecution(executionId);
    if (!execution) {
      // Create a minimal execution object if not tracked
      execution = {
        execution_id: executionId,
        status: 'RUNNING',
        progress: 0,
        current_node: null,
        completed_nodes: 0,
        total_nodes: 0,
        final_output: null,
        error_message: null,
        started_at: new Date().toISOString(),
        completed_at: null,
        execution_source: 'chatbot',
      };
      this.progressTracker.trackExecution(execution);
    }
    
    // Start the resilient stream
    await this.startResilientStream(executionId, handlers);
  }

  /**
   * Start resilient stream for execution
   */
  private async startResilientStream(
    executionId: string,
    handlers: AsyncExecutionHandlers,
    attempt: number = 1
  ): Promise<void> {
    
    // Update connection state
    const connection: StreamConnection = {
      executionId,
      status: attempt === 1 ? 'connecting' : 'reconnecting',
      attempts: attempt,
    };
    this.connectionManager.updateConnection(connection);
    handlers.onConnectionUpdate?.(connection);

    try {
      const controller = new AbortController();
      this.activeStreams.set(executionId, controller);

      const response = await fetch(
        `${baseUrl}/chat-interface/executions/${executionId}/stream`,
        {
          method: 'GET',
          headers: {
            'Accept': 'text/event-stream',
            'Cache-Control': 'no-cache',
            ...(getAccessToken() && { 'Authorization': `Bearer ${getAccessToken()}` }),
          },
          signal: controller.signal,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      if (!response.body) {
        throw new Error('Response body is null');
      }

      // Update connection to connected
      connection.status = 'connected';
      connection.lastConnectedAt = new Date().toISOString();
      this.connectionManager.updateConnection(connection);
      handlers.onConnectionUpdate?.(connection);

      // Process stream
      await processExecutionStream(
        response.body.getReader(),
        (event) => this.handleProgressEvent(executionId, event, handlers),
        controller.signal
      );

      // Stream completed
      this.cleanupStream(executionId);

    } catch (error: any) {
      if (error.name === 'AbortError') {
        return;
      }

      // Handle connection failure
      connection.status = 'failed';
      connection.lastError = error.message;
      this.connectionManager.updateConnection(connection);
      handlers.onConnectionUpdate?.(connection);

      // Retry if appropriate
      if (executionUtils.shouldRetryConnection(error, attempt, DEFAULT_ASYNC_CONFIG.maxRetryAttempts)) {
        const delay = executionUtils.calculateRetryDelay(attempt, DEFAULT_ASYNC_CONFIG);
        
        const timeout = setTimeout(() => {
          this.retryTimeouts.delete(executionId);
          this.startResilientStream(executionId, handlers, attempt + 1);
        }, delay);
        
        this.retryTimeouts.set(executionId, timeout);
      } else {
        handlers.onConnectionError?.(error);
        this.cleanupStream(executionId);
      }
    }
  }

  /**
   * Handle progress event
   */
  private handleProgressEvent(
    executionId: string,
    event: ExecutionProgressEvent,
    handlers: AsyncExecutionHandlers
  ): void {
    
    const execution = this.progressTracker.updateFromProgressEvent(event);
    if (!execution) return;

    // Always forward the progress event
    handlers.onExecutionProgress?.(event);

    // Handle completion states
    if (event.type === 'execution_finished' || 
        (event.type === 'status_update' && event.status === 'COMPLETED')) {
      handlers.onExecutionCompleted?.(execution);
      this.cleanupStream(executionId);
    } else if (event.type === 'status_update' && event.status === 'FAILED') {
      handlers.onExecutionFailed?.(execution, event.error_message || 'Execution failed');
      this.cleanupStream(executionId);
    } else if (event.type === 'status_update' && event.status === 'CANCELLED') {
      handlers.onExecutionCancelled?.(execution);
      this.cleanupStream(executionId);
    }
  }

  /**
   * Cancel execution
   */
  async cancelExecution(executionId: string): Promise<void> {
    await cancelExecution(executionId);
    this.cleanupStream(executionId);
  }

  /**
   * Cleanup stream resources
   */
  private cleanupStream(executionId: string): void {
    const controller = this.activeStreams.get(executionId);
    if (controller) {
      controller.abort();
      this.activeStreams.delete(executionId);
    }

    const timeout = this.retryTimeouts.get(executionId);
    if (timeout) {
      clearTimeout(timeout);
      this.retryTimeouts.delete(executionId);
    }

    this.connectionManager.removeConnection(executionId);
  }

  /**
   * Get connection health
   */
  getConnectionHealth() {
    return this.connectionManager.getConnectionHealth();
  }

  /**
   * Cleanup all
   */
  cleanup(): void {
    for (const executionId of this.activeStreams.keys()) {
      this.cleanupStream(executionId);
    }
    this.progressTracker.clear();
    this.connectionManager.clear();
  }
}

// =================== Section 5: Exported Instance & Functions ===================

export const chatInterfaceAsyncManager = new ChatInterfaceAsyncManager();

export const startResilientExecutionStream = async (
  executionId: string,
  onUpdate: (event: ExecutionProgressEvent) => void,
  onConnectionUpdate: (connection: StreamConnection) => void
): Promise<() => void> => {
  // Create handlers for the stream
  const handlers: AsyncExecutionHandlers = {
    onExecutionProgress: onUpdate,
    onConnectionUpdate: onConnectionUpdate,
    onExecutionCompleted: (execution) => {
      // Notify completion through progress event
      const event: ExecutionProgressEvent = {
        type: 'execution_finished',
        execution_id: execution.execution_id,
        status: 'COMPLETED',
        final_output: execution.final_output,
        progress: 100,
        timestamp: new Date().toISOString()
      };
      onUpdate(event);
    },
    onExecutionFailed: (execution, error) => {
      // Notify failure through progress event
      const event: ExecutionProgressEvent = {
        type: 'status_update',
        execution_id: execution.execution_id,
        status: 'FAILED',
        error_message: error,
        timestamp: new Date().toISOString()
      };
      onUpdate(event);
    }
  };

  // Start the resilient stream using the manager's private method
  // We need to directly start streaming here
  await chatInterfaceAsyncManager.startStreamForExecution(executionId, handlers);
  
  // Return cleanup function
  return () => {
    chatInterfaceAsyncManager.cancelExecution(executionId);
  };
};

export const cleanupAsyncExecutions = () => {
  chatInterfaceAsyncManager.cleanup();
};